import { Component, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';

import { FileManagerService } from '../../../../services/main/file-manager.service';
import File from 'src/app/models/File';
import { CameraCaptureModalComponent } from '../../camera-capture-modal/camera-capture-modal.component';
import {
    FotoVisor,
    ImageViewerComponent,
} from '../image-viewer/image-viewer.component';

@Component({
    selector: 'app-file-manager',
    templateUrl: './file-manager.component.html',
    styleUrls: ['./file-manager.component.scss'],
    standalone: false
})
export class FileManagerComponent implements OnChanges {
    @Input() article_id!: string;

    constructor(private fileManagerService: FileManagerService) {}

    path: string[] = [];
    files: File[] = [];
    ngOnChanges(changes: SimpleChanges) {
        this.setPath([]);
    }

    updateFiles() {
        return new Promise(resolve => {
            this.fileManagerService
                .getArticleFiles(this.article_id, this.path)
                .then(data => {
                    if (!data) return resolve(false);

                    const { isFile } = data;
                    if (isFile) {
                        const { file } = data;
                        if (!file) return resolve(false);
                        this.files = [];
                        this.isPathFile = true;
                        this.pathFile = file;
                        this.isPDFLoaded = false;
                    } else {
                        const { files } = data;
                        if (!files) return resolve(false);
                        this.files = files;
                        this.isPathFile = false;
                        this.isPDFLoaded = false;
                    }

                    // Las miniaturas que fallaron son de la carpeta anterior:
                    // si no se limpiara, un `IMG_01.jpg` roto en una carpeta
                    // taparía el `IMG_01.jpg` bueno de otra.
                    this.miniaturasRotas.clear();

                    resolve(true);
                });
        });
    }

    isPathFile = false;
    pathFile!: File;
    updatingPath = true;
    async setPath(path: string[]) {
        this.path = path;

        this.updatingPath = true;

        const succeed = await this.updateFiles();
        if (!succeed) {
            this.path = this.path.slice(0, this.path.length - 1);
        }

        this.updatingPath = false;
    }

    isImage(file: File) {
        // `isImage` lo decide ahora el backend, que es quien sabe de qué
        // ficheros puede sacar miniatura. Se conserva la comprobación por
        // extensión para las respuestas de una versión anterior de la API.
        if (file.isImage !== undefined) return file.isImage;

        return ['.png', '.jpg', '.jpeg', '.gif'].includes(
            (file.extension || '').toLowerCase()
        );
    }

    isPDFLoaded = false;
    pdfLoaded() {
        this.isPDFLoaded = true;
    }

    getServeFile(file: File) {
        const path = [...(file.path || [])];
        if (!path) return '';

        const fileName = path.splice(path.length - 1, 1)[0];
        let encoded_path = path.join('<>');
        if (encoded_path === '') encoded_path = '<>';

        return (
            this.fileManagerService.getServePath() +
            `${file.authToken}/${encodeURIComponent(
                this.article_id
            )}/${encodeURIComponent(encoded_path)}/${encodeURIComponent(
                fileName
            )}`
        );
    }

    // -- miniaturas, descarga y visor -------------------------------------

    /**
     * Los ficheros cuya miniatura no ha cargado. Se les pinta el icono de
     * siempre en vez de dejar el hueco roto: un `.jpeg` que en realidad no es
     * una foto sigue estando en las carpetas del ERP.
     */
    miniaturasRotas = new Set<string>();

    /** El ancho que se pide al backend. Tiene que ser uno de los suyos. */
    private readonly ANCHO_MINIATURA = 320;

    urlMiniatura(file: File) {
        if (!file.firma) return '';

        return this.fileManagerService.getMediaUrl(
            'mini',
            file.firma,
            this.ANCHO_MINIATURA
        );
    }

    urlDescarga(file: File) {
        if (!file.firma) return '';

        return this.fileManagerService.getMediaUrl('descarga', file.firma);
    }

    /** Si toca pintar miniatura en vez del icono genérico. */
    tieneMiniatura(file: File) {
        return (
            !file.isFolder &&
            !!file.firma &&
            this.isImage(file) &&
            !this.miniaturasRotas.has(this.claveDe(file))
        );
    }

    miniaturaFallida(file: File) {
        this.miniaturasRotas.add(this.claveDe(file));
    }

    /** La firma identifica al fichero Y a su versión; el nombre no basta. */
    private claveDe(file: File) {
        return file.firma || file.name;
    }

    /**
     * Propiedad de flecha y no método: Angular invoca la función de `trackBy`
     * suelta, sin enlazar el componente, así que un método normal se queda sin
     * `this` y revienta en el primer pintado de la rejilla.
     */
    trackFile = (_: number, file: File) => this.claveDe(file);

    @ViewChild('visor') visor!: ImageViewerComponent;

    /** Las fotos de la carpeta actual, en el orden en el que se ven. */
    private get fotosDeLaCarpeta(): File[] {
        return this.files.filter(f => this.tieneMiniatura(f));
    }

    private aFotoVisor(file: File): FotoVisor {
        return {
            nombre: file.name,
            descripcion: file.description,
            urlVer: this.fileManagerService.getMediaUrl('ver', file.firma || ''),
            urlDescarga: this.urlDescarga(file),
        };
    }

    abrirVisor(file: File) {
        if (!file.firma) return;

        // Desde la rejilla se pasa la carpeta entera, para poder ir de una foto
        // a la siguiente deslizando. Desde la vista de detalle `files` está
        // vacío —`updateFiles` lo vacía en la rama de fichero—, así que se abre
        // el visor con esa sola foto en vez de con una lista vacía.
        const fotos = this.fotosDeLaCarpeta;
        const posicion = fotos.indexOf(file);

        if (posicion < 0) {
            this.visor.abrir([this.aFotoVisor(file)], 0);
            return;
        }

        this.visor.abrir(fotos.map(f => this.aFotoVisor(f)), posicion);
    }

    @ViewChild('camCaptureModal') camCaptureModal!: CameraCaptureModalComponent;
    openCapCaptureModal() {
        this.camCaptureModal.openModal();
    }

    fileClicked(file: File) {
        if (this.updatingPath) return;

        // Una foto se amplía; ya no hay que "entrar" en ella para verla, que
        // era navegar a una vista con la imagen a tamaño de columna.
        if (!file.isFolder && this.tieneMiniatura(file)) {
            this.abrirVisor(file);
            return;
        }

        this.setPath([...this.path, file.name]);
    }

    backFolder(index: number) {
        this.setPath(this.path.slice(0, index + 1));
    }
}
